import styles from "./Home.module.css";
import { useEffect, useState, type KeyboardEvent } from "react";
import {
  FILTER_ALL_SELECT_VALUE,
  optionsOrigin,
  optionsType,
  regulation,
  regulationTooltipText,
  REGULATION_EMPHASIS_WORD,
} from "../../Components/Helper";
import { useApiErrorToast } from "../../Components/useApiErrorToast";
import { handleTypeService } from "../../Components/Consts";
import images from "../../Assets/images-removebg-preview(1).png";
import { Footer } from "../../Components/Footer/Footer";
import {
  Button as GetiButton,
  Calendar,
  Input as GetiInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  buttonVariants,
  cn,
} from "@geti/identidade-visual-react";
import { useHome } from "./useHome";
import { HomeDataTable } from "./components/HomeDataTable";
import { HomeFavoriteDataTable } from "./components/HomeFavoriteDataTable";
import Tooltips from "../../Components/Tooltip/Tooltip";
import "dayjs/locale/pt-br";
import dayjs from "dayjs";
import { LuCalendar } from "react-icons/lu";
import {
  MdFilterList,
  MdFilterListOff,
  MdInfoOutline,
  MdSearch,
} from "react-icons/md";

const Home = () => {
  const [isFromDateCalendarOpen, setIsFromDateCalendarOpen] = useState(false);
  const [isToDateCalendarOpen, setIsToDateCalendarOpen] = useState(false);
  const [fromCalendarMonth, setFromCalendarMonth] = useState(() => new Date());
  const [toCalendarMonth, setToCalendarMonth] = useState(() => new Date());
  const keywordTooltipMessage =
    "Separe as palavras por espaço. Cada palavra será filtrada individualmente.";

  const {
    deletePublishedResponse,
    setPublishedError,
    response,
    allPostsResponse,
    isSearched,
    setIsSearched,
    hasSearchedBefore,
    isResponsive,
    setPage,
    handleToggleReverse,
    reversePosts,
    extracted,
    page,
    tableTitle,
    tableTotal,
    tableIsEmpty,
    tableLoading,
    tableError,
    form,
    setForm,
    setHasValue,
    handleDateChange,
    handleDisable,
    handleSubmitFilters,
    handleClearFiltersForm,
  } = useHome();

  useApiErrorToast(
    deletePublishedResponse.error,
    false,
    handleTypeService.deletePublishedError
  );
  useApiErrorToast(isSearched ? response.error : false, true);
  useApiErrorToast(!isSearched ? allPostsResponse.error : false);

  const handleTypeChange = (value: string | null) => {
    setForm((prev) => ({
      ...prev,
      post_type:
        !value || value === FILTER_ALL_SELECT_VALUE ? [] : [value],
    }));
  };

  const handleOriginChange = (value: string | null) => {
    setForm((prev) => ({
      ...prev,
      post_origin:
        !value || value === FILTER_ALL_SELECT_VALUE ? [] : [value],
    }));
  };

  const handlePostCodeChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      post_code: value.replace(/\D/g, ""),
    }));
  };

  const handleKeywordChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      words: value.split(" "),
    }));
  };

  useEffect(() => {
    if (!deletePublishedResponse.error) return;

    const timeoutId = setTimeout(() => {
      setPublishedError(false);
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [deletePublishedResponse.error, setPublishedError]);

  useEffect(() => {
    if (!(isSearched && response.error)) return;

    const timeoutId = setTimeout(() => {
      setIsSearched(false);
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [isSearched, response.error, setIsSearched]);

  const handleFromDatePopoverOpenChange = (open: boolean) => {
    setIsFromDateCalendarOpen(open);
    if (open) {
      setFromCalendarMonth(form.date.from?.toDate() ?? new Date());
    }
  };

  const handleToDatePopoverOpenChange = (open: boolean) => {
    setIsToDateCalendarOpen(open);
    if (open) {
      setToCalendarMonth(form.date.to?.toDate() ?? new Date());
    }
  };

  const submitFiltersFromKeyboard = () => {
    if (handleDisable()) return;

    setIsFromDateCalendarOpen(false);
    setIsToDateCalendarOpen(false);
    handleSubmitFilters();
  };

  const handleFilterControlKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    event.stopPropagation();
    submitFiltersFromKeyboard();
  };

  const renderKeywordInput = () => (
    <div className={styles.keywordInputWithHelp}>
      <GetiInput
        className="h-8 w-full"
        placeholder="Palavras-chave"
        value={form.words.join(" ")}
        onChange={(e) => handleKeywordChange(e.target.value)}
      />
      <Tooltips placement="top" message={keywordTooltipMessage}>
        <button
          type="button"
          aria-label="Ajuda sobre palavras-chave"
          className={styles.keywordHelpButton}
        >
          <MdInfoOutline className="size-5 shrink-0" aria-hidden />
        </button>
      </Tooltips>
    </div>
  );

  return (
    <div className={styles.container}>
      <div
        className={`${hasSearchedBefore ? styles.stableDiv : styles.motionDiv}`}
      >
        {hasSearchedBefore ? (
          <div id="regulationContainer" className={styles.regulationContainer}>
            <Tooltips
              placement="top"
              message={regulationTooltipText}
              wrapperSx={{ display: "block", width: "100%", minWidth: 0 }}
            >
              <div className={styles.regulationCallout} role="note">
                <p className={styles.regulation}>
                  {regulation.startsWith(REGULATION_EMPHASIS_WORD) ? (
                    <>
                      <span className={styles.regulationAtos}>
                        {REGULATION_EMPHASIS_WORD}
                      </span>
                      {regulation.slice(REGULATION_EMPHASIS_WORD.length)}
                    </>
                  ) : (
                    regulation
                  )}
                </p>
              </div>
            </Tooltips>
          </div>
        ) : (
          <div className={styles.IconContainer}>
            <div>
              <img className={styles.Icon} src={images} alt="Icone Def" />
            </div>
            <div className={styles.TextContainer}>
              <h1 className={styles.Title}>
                Defensoria Pública do Estado de Santa Catarina
              </h1>
              <p className={styles.Def2}>
                ENCONTRE AQUI: ATOS, RESOLUÇÕES, DELIBERAÇÕES, PROVIMENTOS,
                MANIFESTAÇÕES, SUMULAS E MUITO MAIS.
              </p>
            </div>
          </div>
        )}
        {isResponsive && (
          <div id="favoriteTableBody" className={styles.favoriteTable}>
            <HomeFavoriteDataTable title={"Destaques"} />
          </div>
        )}

        <form
          id="searchContainer"
          className={cn(styles.searchContainer, styles.searchWrapper)}
          onSubmit={(event) => {
            event.preventDefault();
            submitFiltersFromKeyboard();
          }}
        >
          <div className={styles.titleContainer}>
            <h2 className="text-foreground">Pesquisa de Atos</h2>
          </div>
          {isResponsive && (
            <div className={styles.mobileFiltersToolbar}>
              <Popover>
                <PopoverTrigger
                  type="button"
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-8 items-center gap-2 bg-white px-3 font-normal",
                  )}
                >
                  <MdFilterList className="size-4" aria-hidden />
                  Filtros
                </PopoverTrigger>
                <PopoverContent className="w-[min(100vw-2rem,500px)] p-3">
                  <div className={styles.mobileFiltersPanel}>
                    <div
                      className={cn(
                        "grid w-full grid-cols-1 gap-2 justify-items-stretch",
                      )}
                    >
                      {/* DATA INICIAL */}
                      <div className="w-full" id="calendarsContainer">
                        <Popover
                          open={isFromDateCalendarOpen}
                          onOpenChange={handleFromDatePopoverOpenChange}
                        >
                          <PopoverTrigger
                            type="button"
                            className={cn(
                              buttonVariants({ variant: "outline" }),
                              "h-8 w-full min-w-0 justify-start gap-2 bg-white px-2.5 font-normal",
                            )}
                            onKeyDown={handleFilterControlKeyDown}
                          >
                            <LuCalendar className="size-4 text-muted-foreground" />
                            <span
                              className={cn(
                                "truncate",
                                !form.date.from && "text-muted-foreground",
                              )}
                            >
                              {form.date.from
                                ? form.date.from.format("DD/MM/YYYY")
                                : "Data inicial"}
                            </span>
                          </PopoverTrigger>

                          <PopoverContent className="p-0">
                            <Calendar
                              mode="single"
                              selected={form.date.from?.toDate()}
                              month={fromCalendarMonth}
                              onMonthChange={setFromCalendarMonth}
                              onSelect={(date) => {
                                handleDateChange(
                                  date ? dayjs(date) : null,
                                  "from",
                                );
                                setIsFromDateCalendarOpen(false);
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* DATA FINAL */}
                      <div className="w-full" id="calendarsContainer">
                        <Popover
                          open={isToDateCalendarOpen}
                          onOpenChange={handleToDatePopoverOpenChange}
                        >
                          <PopoverTrigger
                            type="button"
                            className={cn(
                              buttonVariants({ variant: "outline" }),
                              "h-8 w-full min-w-0 justify-start gap-2 bg-white px-2.5 font-normal",
                            )}
                            onKeyDown={handleFilterControlKeyDown}
                          >
                            <LuCalendar className="size-4 text-muted-foreground" />
                            <span
                              className={cn(
                                "truncate",
                                !form.date.to && "text-muted-foreground",
                              )}
                            >
                              {form.date.to
                                ? form.date.to.format("DD/MM/YYYY")
                                : "Data final"}
                            </span>
                          </PopoverTrigger>

                          <PopoverContent className="p-0">
                            <Calendar
                              mode="single"
                              selected={form.date.to?.toDate()}
                              month={toCalendarMonth}
                              onMonthChange={setToCalendarMonth}
                              onSelect={(date) => {
                                handleDateChange(
                                  date ? dayjs(date) : null,
                                  "to",
                                );
                                setIsToDateCalendarOpen(false);
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* TIPO */}
                      <div className="w-full">
                        <Select
                          value={
                            form.post_type[0] ?? FILTER_ALL_SELECT_VALUE
                          }
                          onValueChange={handleTypeChange}
                        >
                          <SelectTrigger
                            id="home-filter-post-type-mobile"
                            className="h-8 w-full"
                            onKeyDown={handleFilterControlKeyDown}
                          >
                            <SelectValue>
                              {(value: string) =>
                                value === FILTER_ALL_SELECT_VALUE
                                  ? "Filtrar por tipo"
                                  : value
                              }
                            </SelectValue>
                          </SelectTrigger>

                          <SelectContent>
                            <SelectItem value={FILTER_ALL_SELECT_VALUE}>
                              Todos
                            </SelectItem>
                            {optionsType.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* ORIGEM */}
                      <div className="w-full">
                        <Select
                          value={
                            form.post_origin[0] ?? FILTER_ALL_SELECT_VALUE
                          }
                          onValueChange={handleOriginChange}
                        >
                          <SelectTrigger
                            id="home-filter-post-origin-mobile"
                            className="h-8 w-full"
                            onKeyDown={handleFilterControlKeyDown}
                          >
                            <SelectValue>
                              {(value: string) =>
                                value === FILTER_ALL_SELECT_VALUE
                                  ? "Filtrar por origem"
                                  : value
                              }
                            </SelectValue>
                          </SelectTrigger>

                          <SelectContent>
                            <SelectItem value={FILTER_ALL_SELECT_VALUE}>
                              Todos
                            </SelectItem>
                            {optionsOrigin.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* NÚMERO */}
                      <div className="w-full">
                        <GetiInput
                          className="h-8 w-full"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="Número"
                          value={form.post_code}
                          onChange={(e) => handlePostCodeChange(e.target.value)}
                        />
                      </div>

                      {/* PALAVRA-CHAVE */}
                      <div className="w-full">
                        {renderKeywordInput()}
                      </div>
                    </div>
                    <div className={styles.filterActions}>
                      <div className={styles.info}>
                        <GetiButton
                          className="inline-flex items-center gap-2"
                          type="submit"
                          disabled={handleDisable()}
                        >
                          <MdSearch className="size-4 shrink-0" aria-hidden />
                          Pesquisar
                        </GetiButton>
                        <GetiButton
                          className="inline-flex items-center gap-2"
                          variant="destructive"
                          type="button"
                          onClick={handleClearFiltersForm}
                          disabled={handleDisable()}
                        >
                          <MdFilterListOff className="size-4 shrink-0" aria-hidden />
                          Limpar filtros
                        </GetiButton>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
          {!isResponsive && (
            <div className={styles.mobileFiltersPanel}>
              <div
                className={cn(
                  "grid w-full grid-cols-1 gap-2 justify-items-stretch sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
                )}
              >
                {/* DATA INICIAL */}
                <div className="w-full" id="calendarsContainer">
                  <Popover
                    open={isFromDateCalendarOpen}
                    onOpenChange={handleFromDatePopoverOpenChange}
                  >
                    <PopoverTrigger
                      type="button"
                      className={cn(
                        buttonVariants({ variant: "outline" }),
                        "h-8 w-full min-w-0 justify-start gap-2 bg-white px-2.5 font-normal",
                      )}
                      onKeyDown={handleFilterControlKeyDown}
                    >
                      <LuCalendar className="size-4 text-muted-foreground" />
                      <span
                        className={cn(
                          "truncate",
                          !form.date.from && "text-muted-foreground",
                        )}
                      >
                        {form.date.from
                          ? form.date.from.format("DD/MM/YYYY")
                          : "Data inicial"}
                      </span>
                    </PopoverTrigger>

                    <PopoverContent className="p-0">
                      <Calendar
                        mode="single"
                        selected={form.date.from?.toDate()}
                        month={fromCalendarMonth}
                        onMonthChange={setFromCalendarMonth}
                        onSelect={(date) => {
                          handleDateChange(date ? dayjs(date) : null, "from");
                          setIsFromDateCalendarOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* DATA FINAL */}
                <div className="w-full" id="calendarsContainer">
                  <Popover
                    open={isToDateCalendarOpen}
                    onOpenChange={handleToDatePopoverOpenChange}
                  >
                    <PopoverTrigger
                      type="button"
                      className={cn(
                        buttonVariants({ variant: "outline" }),
                        "h-8 w-full min-w-0 justify-start gap-2 bg-white px-2.5 font-normal",
                      )}
                      onKeyDown={handleFilterControlKeyDown}
                    >
                      <LuCalendar className="size-4 text-muted-foreground" />
                      <span
                        className={cn(
                          "truncate",
                          !form.date.to && "text-muted-foreground",
                        )}
                      >
                        {form.date.to
                          ? form.date.to.format("DD/MM/YYYY")
                          : "Data final"}
                      </span>
                    </PopoverTrigger>

                    <PopoverContent className="p-0">
                      <Calendar
                        mode="single"
                        selected={form.date.to?.toDate()}
                        month={toCalendarMonth}
                        onMonthChange={setToCalendarMonth}
                        onSelect={(date) => {
                          handleDateChange(date ? dayjs(date) : null, "to");
                          setIsToDateCalendarOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* TIPO */}
                <div className="w-full">
                  <Select
                    value={
                      form.post_type[0] ?? FILTER_ALL_SELECT_VALUE
                    }
                    onValueChange={handleTypeChange}
                  >
                    <SelectTrigger
                      id="home-filter-post-type-desktop"
                      className="h-8 w-full"
                      onKeyDown={handleFilterControlKeyDown}
                    >
                      <SelectValue>
                        {(value: string) =>
                          value === FILTER_ALL_SELECT_VALUE
                            ? "Filtrar por tipo"
                            : value
                        }
                      </SelectValue>
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value={FILTER_ALL_SELECT_VALUE}>
                        Todos
                      </SelectItem>
                      {optionsType.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* ORIGEM */}
                <div className="w-full">
                  <Select
                    value={
                      form.post_origin[0] ?? FILTER_ALL_SELECT_VALUE
                    }
                    onValueChange={handleOriginChange}
                  >
                    <SelectTrigger
                      id="home-filter-post-origin-desktop"
                      className="h-8 w-full"
                      onKeyDown={handleFilterControlKeyDown}
                    >
                      <SelectValue>
                        {(value: string) =>
                          value === FILTER_ALL_SELECT_VALUE
                            ? "Filtrar por origem"
                            : value
                        }
                      </SelectValue>
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value={FILTER_ALL_SELECT_VALUE}>
                        Todos
                      </SelectItem>
                      {optionsOrigin.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* NÚMERO */}
                <div className="w-full">
                  <GetiInput
                    className="h-8 w-full"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Número"
                    value={form.post_code}
                    onChange={(e) => handlePostCodeChange(e.target.value)}
                  />
                </div>

                {/* PALAVRA-CHAVE */}
                <div className="w-full">
                  {renderKeywordInput()}
                </div>
              </div>
              <div className={styles.filterActions}>
                <div className={styles.info}>
                  <GetiButton
                    className="inline-flex items-center gap-2"
                    type="submit"
                    disabled={handleDisable()}
                  >
                    <MdSearch className="size-4 shrink-0" aria-hidden />
                    Pesquisar
                  </GetiButton>
                  <GetiButton
                    className="inline-flex items-center gap-2"
                    variant="destructive"
                    type="button"
                    onClick={handleClearFiltersForm}
                    disabled={handleDisable()}
                  >
                    <MdFilterListOff className="size-4 shrink-0" aria-hidden />
                    Limpar filtros
                  </GetiButton>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
      {hasSearchedBefore ? (
        <div className={styles.tables}>
          <div id="mainTable" className={styles.mainTable}>
            <HomeDataTable
              data={extracted}
              setPage={setPage}
              page={page}
              total={tableTotal}
              isEmpty={tableIsEmpty}
              loading={tableLoading}
              error={tableError}
              onToggleReverse={handleToggleReverse}
              reversePosts={reversePosts}
              isSearched={isSearched}
            />
          </div>
          {!isResponsive && (
            <div id="favoriteTableBody" className={styles.favoriteTable}>
              <HomeFavoriteDataTable title={"Destaques"} />
            </div>
          )}
        </div>
      ) : (
        <div />
      )}
      <div className={styles.footerArea}>
        <Footer />
      </div>
    </div>
  );
};

export default Home;
// deploy v2.1.0
